import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha } from "@mui/material/styles";
import {
  Box,
  Button,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import { updateDesignCalendarCliente as apiUpdateCliente } from "../../api/DesignCalendar/designCalendarApi";
import { useDesignCalendarClientes } from "./DesignCalendarClientesContext";

const ACCENT = "#0f766e";

function apiMessage(error, fallback) {
  return error?.response?.data?.error || fallback;
}

function ColorSwatch({ value, title, disabled, onChange }) {
  return (
    <Box
      component="label"
      title={title}
      sx={{
        position: "relative",
        display: "inline-flex",
        width: 30,
        height: 30,
        borderRadius: 1.2,
        overflow: "hidden",
        border: "2px solid",
        borderColor: alpha("#1c1917", 0.2),
        bgcolor: value,
        cursor: disabled ? "default" : "pointer",
        flexShrink: 0,
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.35)",
      }}
    >
      <Box
        component="input"
        type="color"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          opacity: 0,
          border: "none",
          padding: 0,
          cursor: disabled ? "default" : "pointer",
        }}
      />
    </Box>
  );
}

export default function DesignCalendarClientesPalette({
  canEdit,
  onCatalogChange,
  updateCliente,
}) {
  const { t } = useTranslation();
  const catalog = useDesignCalendarClientes();
  const [anchorEl, setAnchorEl] = useState(null);
  const [draft, setDraft] = useState(catalog);
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setDraft(catalog);
  }, [catalog]);

  const open = Boolean(anchorEl);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return draft;
    return draft.filter((item) =>
      String(item.label || "")
        .toLowerCase()
        .includes(q),
    );
  }, [draft, query]);

  const saveField = async (id, patch) => {
    if (!canEdit) return;
    setBusyId(id);
    setError("");
    try {
      const save = updateCliente || apiUpdateCliente;
      const data = await save(id, patch);
      if (Array.isArray(data?.clientes)) {
        onCatalogChange?.(data.clientes);
      }
    } catch (err) {
      setError(
        apiMessage(
          err,
          t(
            "designCalendar.clientColorsSaveError",
            "No se pudo guardar el color",
          ),
        ),
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Button
        size="small"
        startIcon={<PaletteOutlinedIcon sx={{ fontSize: 16 }} />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          textTransform: "none",
          fontWeight: 800,
          color: ACCENT,
          border: "1px solid",
          borderColor: alpha(ACCENT, 0.28),
          borderRadius: 999,
          px: 1.1,
        }}
      >
        {t("designCalendar.clientColors", "Colores marcas")}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setQuery("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: "1px solid",
            borderColor: alpha(ACCENT, 0.2),
            boxShadow: "0 18px 48px rgba(28, 25, 23, 0.16)",
            width: 380,
            maxWidth: "calc(100vw - 24px)",
          },
        }}
      >
        <Box sx={{ p: 1.4 }}>
          <Typography sx={{ fontWeight: 900, fontSize: "0.9rem", mb: 0.35 }}>
            {t("designCalendar.clientColorsTitle", "Colores por marca")}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.72rem",
              opacity: 0.7,
              mb: 1.1,
              lineHeight: 1.35,
            }}
          >
            {canEdit
              ? t(
                  "designCalendar.clientColorsHint",
                  "Marcas del CRM. Fondo de casilla y acento; se actualiza al momento.",
                )
              : t(
                  "designCalendar.clientColorsReadOnly",
                  "Solo Agustina o Miguel pueden cambiar los colores.",
                )}
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t(
              "designCalendar.clientColorsSearch",
              "Buscar marca…",
            )}
            sx={{ mb: 1 }}
          />
          <Stack
            spacing={0.85}
            sx={{ maxHeight: 420, overflow: "auto", pr: 0.3 }}
          >
            {visible.map((item) => {
              const disabled = !canEdit || Boolean(busyId);
              return (
                <Stack
                  key={item.id}
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{
                    px: 0.7,
                    py: 0.55,
                    borderRadius: 2,
                    bgcolor: item.fill,
                    border: "1px solid",
                    borderColor: alpha(item.color, 0.35),
                    opacity: busyId === item.id ? 0.65 : 1,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: "0.78rem",
                        fontWeight: 800,
                        color: "#1c1917",
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.62rem",
                        opacity: 0.65,
                        fontWeight: 600,
                      }}
                    >
                      {t("designCalendar.clientFill", "Fondo")} ·{" "}
                      {t("designCalendar.clientAccent", "Acento")}
                    </Typography>
                  </Box>
                  <ColorSwatch
                    value={item.fill}
                    title={t("designCalendar.clientFill", "Fondo")}
                    disabled={disabled}
                    onChange={(fill) => {
                      setDraft((prev) =>
                        prev.map((row) =>
                          row.id === item.id ? { ...row, fill } : row,
                        ),
                      );
                      saveField(item.id, { fill });
                    }}
                  />
                  <ColorSwatch
                    value={item.color}
                    title={t("designCalendar.clientAccent", "Acento")}
                    disabled={disabled}
                    onChange={(color) => {
                      setDraft((prev) =>
                        prev.map((row) =>
                          row.id === item.id ? { ...row, color } : row,
                        ),
                      );
                      saveField(item.id, { color });
                    }}
                  />
                </Stack>
              );
            })}
            {!visible.length ? (
              <Typography sx={{ fontSize: "0.75rem", opacity: 0.65, py: 1 }}>
                {t(
                  "designCalendar.clientColorsEmpty",
                  "Ninguna marca coincide",
                )}
              </Typography>
            ) : null}
          </Stack>
          {error ? (
            <Typography sx={{ mt: 1, fontSize: "0.72rem", color: "#b91c1c" }}>
              {error}
            </Typography>
          ) : null}
        </Box>
      </Popover>
    </>
  );
}
